import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, downloadFile } from "@/lib/api";
import { FilePdf } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

const TONE = {
  pending:  "border-amber-200 bg-amber-50 text-amber-800",
  approved: "border-emerald-200 bg-emerald-50 text-emerald-800",
  rejected: "border-rose-200 bg-rose-50 text-rose-800",
};

export function DisposalRequestsDialog({ open, onOpenChange, isAdmin, onDecided }) {
  const [rows, setRows] = useState([]);
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState(null);

  async function load() {
    try { const { data } = await api.get("/disposal-requests"); setRows(data); } catch { toast.error("Failed to load requests"); }
  }
  useEffect(() => { if (open) load(); }, [open]);

  async function decide(r, action) {
    setBusy(r.id);
    try {
      await api.post(`/disposal-requests/${r.id}/${action}`, { note: notes[r.id] || null });
      toast.success(`Disposal ${action === "approve" ? "approved — stock out recorded" : "rejected"}`);
      window.dispatchEvent(new Event("notices:refresh"));
      await load(); onDecided?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Action failed"); }
    finally { setBusy(null); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="disposal-requests-dialog">
        <DialogHeader><DialogTitle className="font-heading font-black">Disposal Clearance Requests</DialogTitle></DialogHeader>
        <div className="border border-border rounded-md overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Item</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Requested By</TableHead>
                <TableHead>Status</TableHead>
                {isAdmin && <TableHead className="text-right">Decision</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="py-6 text-center text-sm text-muted-foreground">No disposal requests yet.</TableCell></TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.id} data-testid={`disposal-row-${r.id}`}>
                  <TableCell className="font-mono text-xs">{(r.created_at || "").slice(0, 10)}</TableCell>
                  <TableCell className="font-medium">{r.item_name}<div className="text-[10px] font-mono text-muted-foreground">{r.sku || ""}</div></TableCell>
                  <TableCell className="text-right tabular-nums">{r.quantity} {r.unit}</TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-[220px]">{r.reason || "—"}</TableCell>
                  <TableCell className="text-sm">{r.requested_by_name}</TableCell>
                  <TableCell>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${TONE[r.status]}`} data-testid={`disposal-status-${r.id}`}>{r.status}</span>
                    {r.status !== "pending" && (
                      <div className="text-[10px] text-muted-foreground mt-1">{r.decided_by_name}{r.decision_note ? ` — ${r.decision_note}` : ""}</div>
                    )}
                  </TableCell>
                  {isAdmin && (
                    <TableCell className="text-right">
                      {r.status === "pending" ? (
                        <div className="flex items-center justify-end gap-2">
                          <Input value={notes[r.id] || ""} onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })} placeholder="Note (optional)" className="h-8 w-40 border-border text-xs" data-testid={`disposal-note-${r.id}`} />
                          <Button size="sm" disabled={busy === r.id} onClick={() => decide(r, "approve")} data-testid={`disposal-approve-${r.id}`}>Approve</Button>
                          <Button size="sm" variant="outline" className="border-border text-rose-700" disabled={busy === r.id} onClick={() => decide(r, "reject")} data-testid={`disposal-reject-${r.id}`}>Reject</Button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-2">
                          <span className="text-xs text-muted-foreground">{(r.decided_at || "").slice(0, 10)}</span>
                          {r.status === "approved" && (
                            <Button size="sm" variant="outline" className="border-border" onClick={async () => {
                              try { await downloadFile(`/disposal-requests/${r.id}/certificate.pdf`, `disposal_certificate_${r.id.slice(0, 8)}.pdf`); }
                              catch { toast.error("Download failed"); }
                            }} data-testid={`disposal-cert-${r.id}`}>
                              <FilePdf size={14} className="mr-1" /> Certificate
                            </Button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
